import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/auth';
import { LoginRequest, RegisterRequest, AuthResponse, User } from '../types';
import { wait } from '@testing-library/user-event/dist/utils';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>; // ✅ return success flag
  register: (email: string, password: string, name: string) => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // ✅ Load existing session on page load
    const token = authService.getToken();
    const currentUser = authService.getCurrentUser();

    if (token && currentUser) {
      setUser(currentUser);
    }
    setLoading(false);
  }, []);

  // ✅ LOGIN (no reload, returns success flag)
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const credentials: LoginRequest = { email, password };
      const response: AuthResponse = await authService.login(credentials);

      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));

      setUser(response.user);
      return true; // success
    } catch (err: any) {
      // ✅ Prevent any page reload or React error propagation
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Invalid credentials. Please try again.';
        wait(1000);
      setError(message);
      return false; // fail, handled gracefully
    } finally {
      setLoading(false);
    }
  };

  // ✅ REGISTER
  const register = async (email: string, password: string, name: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const userData: RegisterRequest = { email, password, name };
      const response: AuthResponse = await authService.register(userData);

      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));

      setUser(response.user);
      return true;
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Registration failed. Please try again.';
      setError(message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ✅ LOGOUT
  const logout = (): void => {
    authService.logout();
    setUser(null);
  };

  // ✅ CLEAR ERROR
  const clearError = (): void => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
